import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Menu, MenuItem } from "@mui/material";
import classNames from "classnames";
import { SmallBody, TinyBody } from "helpers/TypographyComponents";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import variables from "../../../assets/styles/_variables.module.scss";
import type { FilterOption } from "../FiltersAutocomplete";
import styles from "./style.module.scss";

interface ControlFilter {
    /**
     * Name of the filter.
     */
    filterLabel?: string;
    /**
     * All options within filter.
     */
    filterOptions:
        | {
              value: string | number | Array<number | string> | undefined | null;
              label: string;
              count?: number;
              description?: string;
              icon?: React.ElementType;
          }[]
        | FilterOption[];
    /**
     * Value that will be sent to the fetch call.
     */
    controlValue: any;
    controlLabel?: string | undefined;
    /**
     * Function that triggers new fetch in parent component.
     */
    onValueChange: (value: any) => void;
    selector?: boolean;
    btnWidth?: string;
    menuMaxHeight?: string;
    /**
     * True if label displayed above (flex column and not row)
     */
    asColumn?: boolean;
    /**
     * Colors field if true.
     */
    highlight?: boolean;
    minWidth?: string;
}

/**
 * Filter component just like the Control filter in Stock. Options can be displayed horizontally (default) or as menu (selector option)
 */
const ControlFilter = ({
    filterLabel,
    filterOptions,
    controlValue,
    onValueChange,
    selector,
    btnWidth,
    controlLabel,
    menuMaxHeight,
    asColumn = false,
    highlight = false,
    minWidth,
}: ControlFilter) => {
    const [t] = useTranslation();
    /**
     * Option that correspons to "controlValue".
     */
    const controlOption = filterOptions.find((option) => option?.value === controlValue);
    const [chosenValue, setChosenValue] = useState(controlValue);
    const [chosenLabel, setChosenLabel] = useState(controlLabel);
    const [chosenIcon, setChosenIcon] = useState<React.ElementType | undefined>(controlOption?.icon ?? undefined);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleFilterChange = (option: any) => {
        // Sets chosen value to be able to know which button is active.
        setChosenValue(option.value);
        setChosenLabel(option.label);
        option?.icon && setChosenIcon(option.icon);

        // Triggers fetch in parent component.
        onValueChange(option.value);
        setAnchorEl(null);
    };

    const handleSelectorClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const Icon = chosenIcon;

    useEffect(() => {
        if (controlLabel) {
            if (controlLabel !== chosenLabel) setChosenLabel(controlLabel);
        }
    }, [controlLabel, chosenLabel]);

    return (
        <div
            className={classNames({ [styles["control-filter"]]: true, [styles["column"]]: asColumn })}
            style={minWidth ? { minWidth, width: minWidth, maxWidth: minWidth } : undefined}
        >
            {filterLabel && <span>{filterLabel}</span>}
            {!selector && (
                <div className={styles["control-filter-btns"]}>
                    {filterOptions.map((option, i) => {
                        const valueForControl = controlValue !== chosenValue ? controlValue : chosenValue;

                        // Because it's possible to pass an array as value, I need to stringify to compare both.
                        const isOptionActive = Array.isArray(option.value)
                            ? JSON.stringify(option.value) === JSON.stringify(valueForControl)
                            : valueForControl === option.value;

                        return (
                            <button
                                className={classNames({
                                    [styles["active"]]: isOptionActive,
                                })}
                                onClick={() => handleFilterChange(option)}
                                key={`option-${option.value}`}
                            >
                                {option.label} {option.count ? `(${option.count})` : ""}
                            </button>
                        );
                    })}
                </div>
            )}

            {selector && (
                <div className={styles["control-filter-selector"]}>
                    <button
                        onClick={handleSelectorClick}
                        style={{ width: btnWidth }}
                        className={classNames({ [styles["highlight"]]: highlight })}
                    >
                        {Icon && (
                            <div className={styles["label-icon"]}>
                                <Icon />
                            </div>
                        )}
                        <span className={styles["selector-label"]}>
                            {chosenLabel ?? controlLabel ?? t("Filters.AllMasculine")}
                        </span>
                        <div className={styles["btn-arrow"]}>
                            <ExpandMoreIcon sx={{ height: "18px", width: "18px" }} />
                        </div>
                    </button>
                    <Menu
                        id="control-filter-menu"
                        className={styles["control-filter-menu"]}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        MenuListProps={{ sx: { maxHeight: menuMaxHeight, overflow: "auto" } }}
                        PaperProps={{
                            sx: {
                                minWidth: anchorEl instanceof HTMLElement ? anchorEl.clientWidth : undefined,
                            },
                        }}
                    >
                        {filterOptions.map((option) => {
                            const Icon = option?.icon;
                            return (
                                <MenuItem key={`option-${option.value}`} onClick={() => handleFilterChange(option)}>
                                    <div>
                                        <SmallBody className={styles["option-label"]}>
                                            {Icon && <Icon />} {option.label} {option.count ? `(${option.count})` : ""}
                                        </SmallBody>
                                        {option?.description && <TinyBody color="grey">{option.description}</TinyBody>}
                                    </div>
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </div>
            )}
        </div>
    );
};

export default ControlFilter;
